import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "../modal/Modal";
import { CloseButton } from "../input/CloseButton";
import { TextInputField } from "../input/TextInputField";
import { useForm } from "react-hook-form";
import { Button } from "../input/Button";
import { Column } from "../layout/Column";
import { ReactComponent as TrashIcon } from "../icons/trashbin.svg";

import styles from "./ArtworkObjectUrlModal.scss";
import classNames from "classnames";
import { FormattedMessage } from "react-intl";

import { TextAreaInputField } from "../input/TextAreaInputField";
import { SelectInputField } from "../input/SelectInputField";
import { ArtworkNumericInput } from "../input/ArtworkNumericInput";
import { faLink, faUnlink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export function ArtworkUploadModal({
  onSubmit,
  onClose,
  artwork,
  mediums,
  sizes,
  isImage
}) {

  const { handleSubmit, register, watch, setValue } = useForm({
    defaultValues: {
      name: artwork?.el?.components['artwork']?.data?.artworkInfo?.title,
      description: artwork?.el?.components['artwork']?.data?.artworkInfo?.description,
      artistName: artwork?.el?.components['artwork']?.data?.artworkInfo?.author,
      artistStatement: artwork?.el?.components['artwork']?.data?.artworkInfo?.artistStatement,
      date: artwork?.el?.components['artwork']?.data?.artworkInfo?.artworkDate,
      medium: artwork?.el?.components['artwork']?.data?.artworkInfo?.medium,
    }
  });

  const originalWidth = sizes.x || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsX;
  const originalHeight = sizes.y || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsY;
  const originalDepth = sizes.z || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsZ;

  const [aspectRatioActive, setAspectRatioActive] = useState(true);

const calculateAspectRatio = (originalWidth, originalHeight, originalDepth, newWidth = null, newHeight = null, newDepth = null) => {
  if (newWidth !== null) {
    const parsedWidth = parseFloat(newWidth);
    const aspectRatioY = originalHeight / originalWidth;
    const aspectRatioZ = originalDepth / originalWidth;
    const height = (parsedWidth * aspectRatioY).toFixed(2);
    const depth = (parsedWidth * aspectRatioZ).toFixed(2);
    return { width: parsedWidth.toFixed(2), height, depth };
  }
  
  if (newHeight !== null) {
    const parsedHeight = parseFloat(newHeight);
    const aspectRatioX = originalWidth / originalHeight;
    const aspectRatioZ = originalDepth / originalHeight;
    const width = (parsedHeight * aspectRatioX).toFixed(2);
    const depth = (parsedHeight * aspectRatioZ).toFixed(2);
    return { width, height: parsedHeight.toFixed(2), depth };
  }
  
  if (newDepth !== null) {
    const parsedDepth = parseFloat(newDepth);
    const aspectRatioX = originalWidth / originalDepth;
    const aspectRatioY = originalHeight / originalDepth;
    const width = (parsedDepth * aspectRatioX).toFixed(2);
    const height = (parsedDepth * aspectRatioY).toFixed(2);
    return { width, height, depth: parsedDepth.toFixed(2) };
  }

  return {
    width: parseFloat(originalWidth).toFixed(2),
    height: parseFloat(originalHeight).toFixed(2),
    depth: parseFloat(originalDepth).toFixed(2)
  };
};

  const handleDimensionFocus = useCallback((field, defaultValue) => {
    if (!watch(field)) {
      setValue(field, defaultValue);
    }
  }, [setValue, watch]);

const handleDimensionChange = useCallback((dimension, value) => {
  const parsedValue = parseFloat(value);

  if (aspectRatioActive) {
    if (dimension === "dimensionsX") {
      const { height, depth } = calculateAspectRatio(originalWidth, originalHeight, originalDepth, parsedValue);
      setValue("dimensionsY", height);
      setValue("dimensionsZ", depth);
      setValue("dimensionsX", value);  // Use raw input value
    } else if (dimension === "dimensionsY") {
      const { width, depth } = calculateAspectRatio(originalWidth, originalHeight, originalDepth, null, parsedValue);
      setValue("dimensionsX", width);
      setValue("dimensionsZ", depth);
      setValue("dimensionsY", value);  // Use raw input value
    } else if (dimension === "dimensionsZ") {
      const { width, height } = calculateAspectRatio(originalWidth, originalHeight, originalDepth, null, null, parsedValue);
      setValue("dimensionsX", width);
      setValue("dimensionsY", height);
      setValue("dimensionsZ", value);  // Use raw input value
    }
  } else {
    setValue(dimension, value);  // Use raw input value
  }
}, [setValue, originalWidth, originalHeight, originalDepth, aspectRatioActive]);

const handleBlur = useCallback((dimension, value) => {
  const parsedValue = parseFloat(value);
  if (!isNaN(parsedValue)) {
    setValue(dimension, parsedValue.toFixed(2));
  }
}, [setValue]);

  useEffect(() => {
    register("name");
    register("description");
    register("artistName");
    register("artistStatement");
    register("date");
    register("dimensionsX");
    register("dimensionsY");
    register("dimensionsZ");
    register("medium");
  }, [register]);

  const toggleAspectRatio = useCallback(() => {
    setAspectRatioActive(!aspectRatioActive);
  }, [aspectRatioActive]);

  const onChangeMedium = useCallback(
    (value) => {

      setValue("medium", value);
    },
    [ setValue]
  );


const handleKeyPress = (event, property, maxLength) => {
  const inputValue = event.target.value;
  if (inputValue.length >= maxLength && event.key !== 'Backspace') {
    event.preventDefault();
  }
};

const handleInputChange = (event, property, maxLength) => {
  const inputValue = event.target.value;
  if (inputValue.length > maxLength) {
    setValue(property, inputValue.slice(0, maxLength));
  } else {
    setValue(property, inputValue);
  }
};

const handlePaste = (event, property, maxLength) => {
  const inputValue = event.target.value;
  const pastedData = event.clipboardData.getData('text');

  // Calculate how many characters can still be added
  const remainingChars = maxLength - inputValue.length;
  
  // Truncate pasted data if it exceeds the remaining allowed length
  const pastedDataTruncated = pastedData.slice(0, remainingChars);

  // Update the input value with the truncated pasted data
  setValue(property, inputValue + pastedDataTruncated);

  // Prevent the default paste behavior
  event.preventDefault();
};
  const maxLenTitle = 50;
  const maxLenDescription = 70;
  const title = watch("name");
  const description = watch("description");
  const artistName = watch("artistName");
  const artistStatement = watch("artistStatement");

  return (
    <Modal
      id="upload-object-modal"
      title={"Artwork Data"}
      afterTitle={<CloseButton onClick={onClose} />}
      className={styles.modal}
    >
      <Column className="column" as="form" padding center onSubmit={handleSubmit(onSubmit)}>
        <div className="add-input-limit">
          <TextInputField
            name="name"
            placeholder="Enter Title"
            className="input"
            ref={register}
            onChange={(e) => handleInputChange(e, "name", maxLenTitle)}
            onKeyPress={(e) => handleKeyPress(e, "name", maxLenTitle)}
            onPaste={(e) => handlePaste(e, "name", maxLenTitle)}
          />
          <span className="input-limit">{title ? title.length : 0}/{maxLenTitle}</span>
        </div>
        <div className="add-input-limit">
          <TextAreaInputField
            name="description"
            className="textarea"
            autoComplete="off"
            ref={register}
            placeholder={"Enter Description"}
            minRows={2}
            maxRows={3}
            onChange={(e) => handleInputChange(e, "description", maxLenDescription)}
            onKeyPress={(e) => handleKeyPress(e, "description", maxLenDescription)}
            onPaste={(e) => handlePaste(e, "description", maxLenDescription)}
            // onInput={(e) => handleDescriptionChange(e, description, "description")}
            fullWidth
          />

          <span className="input-limit">{description ? description.length : 0}/{maxLenDescription}</span>
        </div>


        <div className="add-input-limit">
          <TextInputField
            ref={register}
            name="artistName"
            placeholder="Artist Name"
            className="input"
            onChange={(e) => handleInputChange(e, "artistName", maxLenTitle)}
          />
          <span className="input-limit">{artistName ? artistName.length : 0}/{maxLenTitle}</span>
        </div>

        <div className="add-input-limit">
          <TextAreaInputField
            className="textarea"
            ref={register}
            name="artistStatement"
            autoComplete="off"
            placeholder={"Artist Statement"}
            minRows={2}
            maxRows={3}
            fullWidth
            onKeyPress={(e) => handleKeyPress(e, "artistStatement", maxLenDescription)}
            onChange={(e) => handleInputChange(e, "artistStatement", maxLenDescription)}
            onPaste={(e) => handlePaste(e, "artistStatement", maxLenDescription)}
          />

          <span className="input-limit">{artistStatement ? artistStatement.length : 0}/{maxLenDescription}</span>
        </div>

        {isImage && (
          <SelectInputField
            name="medium"
            options={mediums.map(medium => ({ value: medium.name, label: medium.name }))}
            ref={register}
            placeholder="Medium"
            className={"select-medium"}
            onChange={onChangeMedium }
          />
        )}

        <TextInputField
          name="date"
          placeholder="Date*"
          ref={register}
          className="input"
        />

        {isImage && (<div className="dimensions">
          <FontAwesomeIcon style={{cursor: "pointer"}} icon={aspectRatioActive ? faLink : faUnlink} onClick={toggleAspectRatio}/>
          <ArtworkNumericInput 
            placeholder="Height (in)" 
            type="number"
            inputMode="decimal"
            step="0.01"
            name="dimensionsY"
            ref={register}
            onFocus={() => handleDimensionFocus('dimensionsY', sizes.y || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsY)}
            onDimensionChange={handleDimensionChange}
            handleBlur={handleBlur}
          />
          <ArtworkNumericInput
            placeholder="Width (in)"
            type="number"
            inputMode="decimal"
            step="0.01"
            name="dimensionsX"
            ref={register}
            onFocus={() => handleDimensionFocus('dimensionsX', sizes.x || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsX)}
            onDimensionChange={handleDimensionChange}
            handleBlur={handleBlur}
          />
          
          {/* <ArtworkNumericInput
            step="0.01"
            placeholder="Depth (in)"
            type="number"
            inputMode="decimal"
            name="dimensionsZ"
            ref={register}
            onFocus={() => handleDimensionFocus('dimensionsZ', sizes.z || artwork?.el?.components['artwork']?.data?.artworkInfo?.dimensionsZ)}
            handleBlur={handleBlur}
          /> */}
          

      </div>)}

        <Button
          type="submit"
          preset="accept"
          className={classNames("place-button")}
        >
          <FormattedMessage id="object-url-modal.save-object-button" defaultMessage="Save" />
        </Button>
      </Column>
    </Modal>
  );
}

ArtworkUploadModal.propTypes = {
  isMobile: PropTypes.bool,
  showModelCollectionLink: PropTypes.bool,
  modelCollectionUrl: PropTypes.string,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func
};

function ThumbnailComponent({ thumbnail, onClear }) {
  return (
    <div className={styles.thumbnail}>
      <div className="remove" onClick={onClear}>
        <TrashIcon size={24} />
      </div>
      <img src={thumbnail}></img>
    </div>
  );
}
